/**
 * Eclairage du monde et brume.
 *
 * Une seule lumiere directionnelle (le soleil, puis la lune la nuit) projette
 * les ombres, plus une HemisphereLight pour le remplissage. La brume
 * (THREE.FogExp2) suit la couleur d'horizon du ciel afin que le lointain se
 * fonde exactement dans le degrade du dome.
 *
 * Les couleurs et intensites viennent d'une table de cles horaires interpolee
 * en douceur, comme pour le ciel: aucun saut de lumiere sur 24 minutes.
 */
import * as THREE from 'three'
import type { Horloge } from '../../shared/sim/temps.ts'
import { hauteurSoleil } from '../../shared/sim/temps.ts'
import { clamp, lerp, melangerCouleur, smoothstep } from '../../shared/sim/alea.ts'
import { directionLune, directionSoleil, encadrerCles, etatCiel } from './ciel.ts'

// ------------------------------------------------------------------ reglages

/** Resolution de la carte d'ombre. */
const TAILLE_OMBRE = 2048

/** Demi-cote du frustum d'ombre, en metres, autour du joueur. */
const DEMI_OMBRE = 35

/** Distance de la source lumineuse a sa cible, en metres. */
const DISTANCE_SOURCE = 90

/**
 * Elevation minimale de la source, en radians (~12 degres). Un soleil pile sur
 * l'horizon donnerait des ombres infinies et sales: on le releve un peu tout
 * en gardant sa couleur rasante.
 */
const ELEVATION_MIN = 0.21

/** Elevation maximale, en radians (~62 degres): garde des ombres lisibles. */
const ELEVATION_MAX = 1.08

/**
 * Sous cette hauteur de soleil, la source devient la lune (direction opposee).
 * La bascule tombe a 5 h 27 et 21 h 03, exactement la ou la table d'intensite
 * est au plus bas (0.10): le changement de direction des ombres est invisible.
 */
const SEUIL_LUNE = -0.12

/** Pas de quantification du frustum d'ombre, en texels. */
const TEXELS_PAR_PAS = 8

/**
 * Hysteresis du recentrage, en pas: l'ancre ne bouge que lorsque la cible s'en
 * est ecartee de plus de cette fraction d'un pas (au lieu de la moitie, qui est
 * ce que fait un simple arrondi).
 */
const HYSTERESIS_PAS = 0.8

/**
 * Marge ajoutee a l'emprise vue, en metres: le relief et la hauteur des arbres
 * et des batiments debordent du plan du sol sur lequel on projette l'ecran, et
 * une ombre se projette depuis un objet parfois HORS de l'ecran.
 */
const MARGE_OMBRE = 10

/** Granularite du demi-cote ajuste a la vue, en metres. */
const PAS_PORTEE = 10

/** Demi-cote maximal du frustum d'ombre ajuste a la vue. */
const DEMI_OMBRE_MAX = 120

/** Part de la couleur du biome dans la brume. */
const PART_BIOME = 0.35

/** Couleur des fenetres eteintes (le jour). */
const FENETRE_ETEINTE = 0x1e1a15

/** Couleur des fenetres allumees (la nuit): ambre chaude. */
const FENETRE_ALLUMEE = 0xffc26a

const HAUT = new THREE.Vector3(0, 1, 0)

// ------------------------------------------------------------ table de cles

interface CleLumiere {
  /** Heure decimale de la cle. */
  readonly h: number
  /** Couleur de la source principale (soleil, ou lune la nuit). */
  readonly source: number
  /** Intensite de la source principale. */
  readonly intensite: number
  /** Couleur du ciel pour la HemisphereLight (venant du haut). */
  readonly ciel: number
  /** Couleur du sol pour la HemisphereLight (rebond venant du bas). */
  readonly sol: number
  /** Intensite de la HemisphereLight. */
  readonly ambiance: number
  /** Densite du FogExp2. */
  readonly brume: number
}

/**
 * Cles d'eclairage. Plein jour: soleil 2.05 / ambiance 0.80. Aube et soir:
 * ~1.0 tres chaud. Nuit: lune 0.18 / ambiance 0.22, bleu argent.
 */
const CLES_LUMIERE: readonly CleLumiere[] = [
  { h: 0.0, source: 0xaec6ff, intensite: 0.18, ciel: 0x28407a, sol: 0x12182c, ambiance: 0.22, brume: 0.0045 },
  { h: 4.0, source: 0xaec6ff, intensite: 0.18, ciel: 0x2c4480, sol: 0x131a2e, ambiance: 0.23, brume: 0.0045 },
  { h: 5.0, source: 0x9fb6f0, intensite: 0.19, ciel: 0x33417e, sol: 0x181a30, ambiance: 0.25, brume: 0.0044 },
  // bascule lune -> soleil
  { h: 5.45, source: 0xa8879c, intensite: 0.1, ciel: 0x413f70, sol: 0x1e1c2e, ambiance: 0.3, brume: 0.0043 },
  { h: 5.75, source: 0xd08a72, intensite: 0.38, ciel: 0x64547c, sol: 0x2a2431, ambiance: 0.37, brume: 0.0041 },
  { h: 6.0, source: 0xffa562, intensite: 0.95, ciel: 0x8a6a86, sol: 0x3a2e30, ambiance: 0.45, brume: 0.0038 },
  { h: 7.0, source: 0xffc98a, intensite: 1.55, ciel: 0x9fc0e8, sol: 0x4a4a3a, ambiance: 0.62, brume: 0.003 },
  { h: 7.5, source: 0xffe0b4, intensite: 1.85, ciel: 0xb6d8f4, sol: 0x55603f, ambiance: 0.72, brume: 0.0026 },
  { h: 12.0, source: 0xfff6e2, intensite: 2.05, ciel: 0xcfe8fb, sol: 0x5a6a45, ambiance: 0.8, brume: 0.0022 },
  { h: 17.0, source: 0xffe9c0, intensite: 1.85, ciel: 0xc4dcf2, sol: 0x5a6242, ambiance: 0.74, brume: 0.0024 },
  { h: 19.0, source: 0xffb066, intensite: 1.15, ciel: 0xa88ab0, sol: 0x4a3a34, ambiance: 0.55, brume: 0.0032 },
  { h: 20.0, source: 0xff8a48, intensite: 1.0, ciel: 0x8a6a92, sol: 0x3e2f34, ambiance: 0.46, brume: 0.0036 },
  { h: 20.5, source: 0xf4653f, intensite: 0.85, ciel: 0x6a4f7a, sol: 0x2e2430, ambiance: 0.38, brume: 0.004 },
  { h: 20.8, source: 0xc4556a, intensite: 0.42, ciel: 0x584472, sol: 0x28202c, ambiance: 0.34, brume: 0.0041 },
  // bascule soleil -> lune
  { h: 21.05, source: 0xa06284, intensite: 0.1, ciel: 0x453a68, sol: 0x201b2a, ambiance: 0.29, brume: 0.0042 },
  { h: 21.6, source: 0xa8b4f0, intensite: 0.15, ciel: 0x36407c, sol: 0x181a30, ambiance: 0.26, brume: 0.0044 },
  { h: 22.0, source: 0xb0c4ff, intensite: 0.18, ciel: 0x2c4080, sol: 0x141a30, ambiance: 0.24, brume: 0.0045 },
  { h: 24.0, source: 0xaec6ff, intensite: 0.18, ciel: 0x28407a, sol: 0x12182c, ambiance: 0.22, brume: 0.0045 },
]

/** Etat d'eclairage interpole pour une heure. */
export interface EtatLumiere {
  source: number
  intensite: number
  ciel: number
  sol: number
  ambiance: number
  brume: number
}

/** Eclairage interpole pour une heure decimale. */
function etatLumiere(heureDecimale: number): EtatLumiere {
  const h = ((heureDecimale % 24) + 24) % 24
  const { a, b, t } = encadrerCles(CLES_LUMIERE, h)
  return {
    source: melangerCouleur(a.source, b.source, t),
    intensite: lerp(a.intensite, b.intensite, t),
    ciel: melangerCouleur(a.ciel, b.ciel, t),
    sol: melangerCouleur(a.sol, b.sol, t),
    ambiance: lerp(a.ambiance, b.ambiance, t),
    brume: lerp(a.brume, b.brume, t),
  }
}

// --------------------------------------------------------------------- brume

/**
 * Pose ou met a jour le FogExp2 de la scene. Reutilise le brouillard existant
 * pour ne pas forcer three.js a recompiler les nuanceurs a chaque appel.
 */
function appliquerBrume(scene: THREE.Scene, couleur: THREE.Color, densite: number): void {
  const actuel = scene.fog
  if (actuel instanceof THREE.FogExp2) {
    actuel.color.copy(couleur)
    actuel.density = densite
    return
  }
  const brouillard = new THREE.FogExp2(0x000000, densite)
  brouillard.color.copy(couleur)
  scene.fog = brouillard
}

/**
 * Couleur emissive des fenetres des maisons: eteinte le jour, ambre chaude la
 * nuit. A appeler avec Lumiere.facteurNuit.
 */
export function couleurFenetre(facteurNuit: number): number {
  const t = smoothstep(0.22, 0.72, clamp(facteurNuit, 0, 1))
  return melangerCouleur(FENETRE_ETEINTE, FENETRE_ALLUMEE, t)
}

/**
 * Facteur de nuit pour une heure donnee: 0 en plein jour, 1 au coeur de la
 * nuit. Calcule a partir de hauteurSoleil(), donc parfaitement continu.
 */
function facteurNuitPour(heureDecimale: number): number {
  return 1 - smoothstep(-0.2, 0.25, hauteurSoleil(heureDecimale))
}

// -------------------------------------------------------------------- classe

export class Lumiere {
  /** Source principale et SEULE lumiere qui projette des ombres. */
  readonly soleil: THREE.DirectionalLight

  /** Remplissage: ciel au dessus, rebond du sol en dessous. */
  readonly ambiance: THREE.HemisphereLight

  /**
   * Multiplicateur de densite de brume, 1 par defaut. Reserve a la meteo: une
   * pluie ou un brouillard peut monter a 3 ou 4 sans toucher a la table.
   */
  multiplicateurBrume = 1

  private readonly scene: THREE.Scene
  private readonly cible: THREE.Object3D
  private readonly couleurBrumeCourante = new THREE.Color(0x0b1330)
  private densite = 0.0022
  private facteurNuitInterne = 0

  private demiOmbre = DEMI_OMBRE
  private ancreX = Number.NaN
  private ancreY = Number.NaN
  private readonly coin = new THREE.Vector3()
  private readonly rayon = new THREE.Vector3()
  private readonly dir = new THREE.Vector3(1, 1, 0)
  private readonly droite = new THREE.Vector3()
  private readonly hautOmbre = new THREE.Vector3()
  private readonly ancre = new THREE.Vector3()

  constructor(scene: THREE.Scene) {
    this.scene = scene

    this.ambiance = new THREE.HemisphereLight(0x28407a, 0x12182c, 0.22)
    this.ambiance.name = 'ambiance'
    scene.add(this.ambiance)

    this.soleil = new THREE.DirectionalLight(0xaec6ff, 0.18)
    this.soleil.name = 'soleil'
    this.soleil.castShadow = true
    // On ne coupe jamais castShadow: le nombre de lumieres a ombre fait partie
    // de la cle de cache des programmes, l'eteindre la nuit provoquerait une
    // recompilation de tous les materiaux deux fois par jour.
    const ombre = this.soleil.shadow
    ombre.mapSize.set(TAILLE_OMBRE, TAILLE_OMBRE)
    ombre.camera.left = -DEMI_OMBRE
    ombre.camera.right = DEMI_OMBRE
    ombre.camera.top = DEMI_OMBRE
    ombre.camera.bottom = -DEMI_OMBRE
    ombre.camera.near = 1
    ombre.camera.far = DISTANCE_SOURCE * 2 + DEMI_OMBRE * 2
    // Un texel fait 70 / 2048 = 0.034 m: normalBias suffit contre l'acne, un
    // bias trop grand decollerait les ombres du pied des objets.
    ombre.bias = -0.0002
    ombre.normalBias = 0.05
    ombre.camera.updateProjectionMatrix()
    scene.add(this.soleil)

    // La cible doit appartenir a la scene pour que sa matrice soit a jour.
    this.cible = new THREE.Object3D()
    this.cible.name = 'soleil-cible'
    scene.add(this.cible)
    this.soleil.target = this.cible

    appliquerBrume(scene, this.couleurBrumeCourante, this.densite)
  }

  /**
   * 0 en plein jour, 1 au coeur de la nuit, avec une transition douce a l'aube
   * et au crepuscule. Valeur publique de reference pour tout ce qui doit
   * reagir a la nuit: reflets et couleur de l'eau, allumage des fenetres,
   * autorisation et eclat des feux d'artifice, lucioles, etc.
   * Vaut 0 de 7 h 30 a 19 h, environ 0.58 au lever et au coucher, 1 de 22 h a
   * 4 h. Toujours dans [0, 1].
   */
  get facteurNuit(): number {
    return this.facteurNuitInterne
  }

  /** Densite courante du FogExp2, multiplicateur meteo compris. */
  get densiteBrume(): number {
    return this.densite
  }

  /**
   * Change le demi-cote du frustum d'ombre (35 m par defaut). Utile si le
   * joueur dezoome beaucoup: au zoom maximal la vue est plus large que la zone
   * ombree, les ombres s'arretent alors a 35 m du joueur. Elargir la zone rend
   * les ombres plus molles (meme carte de 2048 pour une surface plus grande).
   */
  definirPorteeOmbre(demiCote: number): void {
    this.demiOmbre = clamp(demiCote, 10, 120)
    const cam = this.soleil.shadow.camera
    cam.left = -this.demiOmbre
    cam.right = this.demiOmbre
    cam.top = this.demiOmbre
    cam.bottom = -this.demiOmbre
    cam.far = DISTANCE_SOURCE * 2 + this.demiOmbre * 2
    cam.updateProjectionMatrix()
  }

  /**
   * Met a jour l'eclairage et recentre l'ombre sur `cible` (le joueur).
   * `couleurBiome` est la couleur de brume du biome courant (BiomeDef.
   * couleurBrume): elle est melangee a 35 % avec la couleur du ciel.
   */
  maj(h: Horloge, cible: THREE.Vector3, couleurBiome?: number, camera?: THREE.OrthographicCamera): void {
    const hd = h.heureDecimale
    const hs = hauteurSoleil(hd)
    const etat = etatLumiere(hd)
    this.facteurNuitInterne = facteurNuitPour(hd)

    // --- direction de la source: soleil le jour, lune la nuit.
    if (hs < SEUIL_LUNE) directionLune(hd, this.dir)
    else directionSoleil(hd, this.dir)
    this.releverSource()

    // --- couleurs et intensites (jamais d'intensite negative).
    this.soleil.color.setHex(etat.source)
    this.soleil.intensity = Math.max(0, etat.intensite)
    this.ambiance.color.setHex(etat.ciel)
    this.ambiance.groundColor.setHex(etat.sol)
    this.ambiance.intensity = Math.max(0, etat.ambiance)

    // --- ombre serree, recentree et quantifiee sur le joueur.
    this.droite.crossVectors(HAUT, this.dir)
    if (this.droite.lengthSq() < 1e-8) this.droite.set(1, 0, 0)
    else this.droite.normalize()
    this.hautOmbre.crossVectors(this.dir, this.droite).normalize()
    if (camera) this.ajusterPortee(cible, camera)
    this.recentrerOmbre(cible)

    // --- brume accordee a l'horizon du ciel et au biome.
    let teinte = etatCiel(hd).horizon
    if (couleurBiome !== undefined) teinte = melangerCouleur(teinte, couleurBiome, PART_BIOME)
    this.couleurBrumeCourante.setHex(teinte)
    this.densite = etat.brume * this.multiplicateurBrume
    appliquerBrume(this.scene, this.couleurBrumeCourante, this.densite)
  }

  /** Couleur courante de la brume. Copie: libre a l'appelant de la modifier. */
  couleurBrume(): THREE.Color {
    return this.couleurBrumeCourante.clone()
  }

  /** Contraint l'elevation de la source entre ELEVATION_MIN et ELEVATION_MAX. */
  private releverSource(): void {
    const y = clamp(this.dir.y, -1, 1)
    const elevation = clamp(Math.asin(y), ELEVATION_MIN, ELEVATION_MAX)
    const plat = Math.hypot(this.dir.x, this.dir.z)
    const cos = Math.cos(elevation)
    if (plat > 1e-5) {
      this.dir.set((this.dir.x / plat) * cos, Math.sin(elevation), (this.dir.z / plat) * cos)
    } else {
      this.dir.set(cos, Math.sin(elevation), 0)
    }
  }

  /**
   * LE FRUSTUM D'OMBRE COUVRE CE QUE LA CAMERA MONTRE.
   *
   * Il faisait 35 m de demi-cote, fixe: assez au zoom ordinaire, pas une fois
   * dezoome — a 45, l'ecran d'un telephone montre pres de 80 m de profondeur.
   * Le bord du frustum traversait donc l'ecran, et comme il avance par pas de
   * quelques texels, chaque pas de la voiture recoupait autrement les ombres
   * des arbres et des maisons a cheval sur ce bord: une bande d'ombres qui
   * clignotent, et elles seules (vu au telephone le 13 octobre). On projette
   * les quatre coins de l'ecran sur le sol, on les exprime dans le repere de
   * l'ombre, et le demi-cote suit — jamais en dessous de 35 m, par paliers de
   * 10 m, et il ne retrecit qu'avec une bonne marge: un frustum qui changerait
   * de taille a chaque image changerait aussi de grille de texels, et ferait
   * frissonner TOUTES les ombres.
   */
  private ajusterPortee(cible: THREE.Vector3, camera: THREE.OrthographicCamera): void {
    camera.getWorldDirection(this.rayon)
    if (this.rayon.y > -1e-3) return
    let besoin = 0
    for (const [nx, ny] of [
      [-1, -1],
      [1, -1],
      [-1, 1],
      [1, 1],
    ] as const) {
      // Camera orthographique: tous les rayons sont paralleles.
      this.coin.set(nx, ny, -1).unproject(camera)
      const t = (cible.y - this.coin.y) / this.rayon.y
      this.coin.addScaledVector(this.rayon, t).sub(cible)
      besoin = Math.max(besoin, Math.abs(this.coin.dot(this.droite)), Math.abs(this.coin.dot(this.hautOmbre)))
    }
    const voulu = clamp(Math.ceil((besoin + MARGE_OMBRE) / PAS_PORTEE) * PAS_PORTEE, DEMI_OMBRE, DEMI_OMBRE_MAX)
    if (voulu > this.demiOmbre || voulu < this.demiOmbre - PAS_PORTEE) this.definirPorteeOmbre(voulu)
  }

  /**
   * Recentre le frustum d'ombre sur le joueur en quantifiant la position dans
   * le repere de l'ombre, par pas de plusieurs texels: sans cela les bords des
   * ombres frissonnent a chaque pas du joueur.
   *
   * Avec HYSTERESIS: un simple arrondi bascule d'un pas a l'autre des que la
   * cible frole la moitie d'un pas, et une cible qui tremble d'un millimetre
   * la-dessus fait sauter le frustum a chaque image.
   */
  private recentrerOmbre(cible: THREE.Vector3): void {
    const pas = ((this.demiOmbre * 2) / TAILLE_OMBRE) * TEXELS_PAR_PAS
    const vx = cible.dot(this.droite)
    const vy = cible.dot(this.hautOmbre)
    // Une ancre qui n'est plus sur la grille (la portee a change) se recalcule.
    const surGrille = (a: number): boolean => Math.abs(a / pas - Math.round(a / pas)) < 1e-6
    if (!(Math.abs(vx - this.ancreX) <= pas * HYSTERESIS_PAS) || !surGrille(this.ancreX)) {
      this.ancreX = Math.round(vx / pas) * pas
    }
    if (!(Math.abs(vy - this.ancreY) <= pas * HYSTERESIS_PAS) || !surGrille(this.ancreY)) {
      this.ancreY = Math.round(vy / pas) * pas
    }
    // Les deux premiers axes sont ceux du plan de la carte d'ombre: c'est leur
    // quantification qui supprime le frissonnement. Le troisieme (profondeur)
    // est quantifie aussi, par simple hygiene.
    const pz = Math.round(cible.dot(this.dir) / pas) * pas

    this.ancre
      .set(0, 0, 0)
      .addScaledVector(this.droite, this.ancreX)
      .addScaledVector(this.hautOmbre, this.ancreY)
      .addScaledVector(this.dir, pz)

    this.cible.position.copy(this.ancre)
    this.cible.updateMatrixWorld()
    this.soleil.position.copy(this.ancre).addScaledVector(this.dir, DISTANCE_SOURCE)
  }

  dispose(): void {
    this.scene.remove(this.soleil)
    this.scene.remove(this.ambiance)
    this.scene.remove(this.cible)
    this.soleil.dispose()
    this.ambiance.dispose()
    this.scene.fog = null
  }
}
